import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { KurtMark } from "./KurtMark";
import { Bot, Hand, ShieldAlert, SlidersHorizontal, CheckCircle2, Zap, Ban } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <div className="py-5 border-b border-white/[0.07] last:border-0">
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-8 h-8 rounded-lg bg-emerald-500/12 text-emerald-400 flex items-center justify-center shrink-0"><Icon size={16} /></span>
      <h3 className="kz-head font-bold text-slate-50">{title}</h3>
    </div>
    <div className="text-sm text-slate-300 leading-relaxed space-y-2 pl-[42px]">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex gap-2"><span className="text-emerald-400 mt-0.5">•</span><span>{children}</span></p>
);

export const UserManual = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="user-manual" className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0B0F17] border border-white/10 text-slate-200 p-0">
      <DialogTitle className="sr-only">The KURT Manual</DialogTitle>
      <DialogDescription className="sr-only">How KURT works, what runs automatically, what needs your approval, and the hard rules he can never break.</DialogDescription>
      <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-5 border-b border-white/10 bg-[#0B0F17]/95 backdrop-blur-xl">
        <KurtMark size={40} />
        <div>
          <h2 className="kz-head text-xl font-extrabold">The KURT Manual</h2>
          <p className="text-xs text-slate-400">One page. Plain English. Everything you need.</p>
        </div>
      </div>
      <div className="px-6 pb-6">
        <Section icon={Bot} title="What KURT is">
          <p>Your AI workforce for a small business. He finds buyers, creates campaigns, sends outreach, holds real conversations, grows your audience, and learns from what works — and he <b className="text-slate-100">does the work himself</b> instead of just telling you what to do.</p>
        </Section>

        <Section icon={CheckCircle2} title="How you work with him">
          <Li>Talk to him in <b>Talk to KURT</b> — tell him what you want done.</Li>
          <Li>Check <b>Today</b> each morning for a 30-second briefing.</Li>
          <Li>Clear <b>Approve</b> in one tap each.</Li>
          <Li>Watch results in <b>Sales & Learning</b>.</Li>
        </Section>

        <Section icon={Zap} title="What runs automatically">
          <p>Prospecting, research, verified enrichment, email outreach and follow-up, email conversations, social publishing to your accounts, SEO and content, experiments, and learning — all without you lifting a finger.</p>
        </Section>

        <Section icon={SlidersHorizontal} title="What needs your approval">
          <p>Spend above your limits, new channels, discounts, claims-sensitive content, and anything the compliance gate flags. It waits for you in <b>Approve</b> — one tap.</p>
        </Section>

        <Section icon={Hand} title="What needs your hands">
          <p>Cold Instagram / LinkedIn DMs and tags (platform rules), final PR and partnership relationships, and high-value closes — KURT hands these to you warm and ready.</p>
        </Section>

        <Section icon={ShieldAlert} title="How you stay in control">
          <Li>Set limits, budgets, tone and brand boundaries.</Li>
          <Li>Override any decision at any time.</Li>
          <Li>Pause everything with one button.</Li>
          <Li>Take over any conversation, or hand it back.</Li>
        </Section>

        <Section icon={Ban} title="The hard rules KURT can never break">
          <p className="text-red-200/90">No fabricated contact details. No contact after opt-out. No prohibited automation. No deception. <b>Compliance overrides revenue, always.</b></p>
        </Section>
      </div>
    </DialogContent>
  </Dialog>
);
